import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';

import { environment } from '../../../environments/environment';

export interface CookieConsentPreferences {
  essential: true;
  analytics: boolean;
  // auth: boolean;
  // Preparado para futuro login público si en algún momento se separa
  // el consentimiento de sesión/autenticación del consentimiento analítico.
}

const COOKIE_CONSENT_STORAGE_KEY = 'web_cookie_consent';

const DEFAULT_COOKIE_CONSENT_PREFERENCES: CookieConsentPreferences = {
  essential: true,
  analytics: false,
};

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  readonly #document = inject(DOCUMENT);

  readonly #preferences = signal<CookieConsentPreferences | null>(null);
  readonly #gtmLoaded = signal<boolean>(false);
  readonly #preferencesOpen = signal<boolean>(false);

  readonly preferences = this.#preferences.asReadonly();
  readonly hasDecision = computed(() => this.#preferences() !== null);
  readonly #initialized = signal<boolean>(false);
  readonly initialized = this.#initialized.asReadonly();

  readonly shouldShowBanner = computed(() =>
    this.#initialized() && (!this.hasDecision() || this.#preferencesOpen())
  ); readonly isPreferencesOpen = this.#preferencesOpen.asReadonly();
  readonly analyticsAccepted = computed(() => !!this.#preferences()?.analytics);

  init(): void {
    const storedPreferences = this.#readStoredPreferences();

    if (storedPreferences) {
      this.#preferences.set(storedPreferences);
      this.#syncAnalytics(storedPreferences);
    }

    this.#initialized.set(true); // siempre al final
  }

  acceptAll(): void {
    this.#commit({
      ...DEFAULT_COOKIE_CONSENT_PREFERENCES,
      analytics: true,
    });
  }

  acceptEssentialOnly(): void {
    this.#commit(DEFAULT_COOKIE_CONSENT_PREFERENCES);
  }

  savePreferences(preferences: Pick<CookieConsentPreferences, 'analytics'>): void {
    this.#commit({
      ...DEFAULT_COOKIE_CONSENT_PREFERENCES,
      analytics: preferences.analytics,
    });
  }

  openPreferences(): void {
    this.#preferencesOpen.set(true);
  }

  #commit(preferences: CookieConsentPreferences): void {
    this.#preferences.set(preferences);
    this.#writeStoredPreferences(preferences);
    this.#syncAnalytics(preferences);
    this.#preferencesOpen.set(false);
  }

  #syncAnalytics(preferences: CookieConsentPreferences): void {
    if (
      !preferences.analytics ||
      !environment.analytics.enabled ||
      !environment.analytics.gtmContainerId
    ) {
      return;
    }

    this.#loadGoogleTagManager(environment.analytics.gtmContainerId);
  }

  #loadGoogleTagManager(containerId: string): void {
    if (this.#gtmLoaded()) {
      return;
    }

    const win = this.#document.defaultView;
    if (!win) {
      return;
    }

    const existingScript = this.#document.getElementById('gtm-script');

    if (existingScript) {
      this.#gtmLoaded.set(true);
      return;
    }

    const dataLayer = ((win as Window & { dataLayer?: unknown[] }).dataLayer ??= []);
    dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    const firstScript = this.#document.getElementsByTagName('script')[0];
    const script = this.#document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;

    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      this.#document.head.appendChild(script);
    }

    this.#gtmLoaded.set(true);
  }

  #readStoredPreferences(): CookieConsentPreferences | null {
    const rawValue = this.#storage()?.getItem(COOKIE_CONSENT_STORAGE_KEY);

    if (!rawValue) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawValue) as Partial<CookieConsentPreferences>;

      return {
        ...DEFAULT_COOKIE_CONSENT_PREFERENCES,
        analytics: !!parsed.analytics,
      };
    } catch {
      return null;
    }
  }

  #writeStoredPreferences(preferences: CookieConsentPreferences): void {
    this.#storage()?.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(preferences));
  }

  #storage(): Storage | null {
    return this.#document.defaultView?.localStorage ?? null;
  }
}
